import {range} from '@benchling/js-utils';
import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {removeGaps} from 'biojs/Util';
import {AntibodyNumberingLayout} from 'constants/MapsConstants';
import {getRealToGapMap} from 'molbio/aa-sequences/alignments/getRealToGapMap';
import AntibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {
  NumberingScheme,
  ValidNumberingResult,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {getMetadataSummary} from 'molbio/antibody-engineering/useAntibodyMetadata';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import Type from 'molbio/maps/Type';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHTEST} from 'ui-library/Colors';

export const MIN_WIDTH_FOR_INDEX = 21;

// The box behind a region's numbers, which starts above the numbers' baseline
const REGION_WITH_NUMBERS_TOP = -5;
const REGION_WITH_NUMBERS_HEIGHT = 15;
// The bar standing in for a region's numbers when residues are too narrow to show them
const REGION_WITHOUT_NUMBERS_HEIGHT = 5;
// The space between the regions of numberings drawn on different layers
const LAYER_GAP = 2;

/**
 * Whether residues of the given width on a slice are wide enough to show their numbers.
 */
function shouldShowNumbers(residueWidth: number): boolean {
  return residueWidth > MIN_WIDTH_FOR_INDEX;
}

/**
 * Returns the distance between the tops of consecutive layers of numberings, for residues of the given
 * width on a slice.
 */
export function getNumberingLayerHeight(residueWidth: number): number {
  return (
    (shouldShowNumbers(residueWidth) ? REGION_WITH_NUMBERS_HEIGHT : REGION_WITHOUT_NUMBERS_HEIGHT) + LAYER_GAP
  );
}

/**
 * Antibody numbering for a stretch of residues.
 */
export interface ResidueNumbering {
  // Ungapped index of the residue the first label belongs to
  start: number;
  // One label per residue from `start`, or null for a residue that has no number
  labels: ReadonlyArray<string | null>;
  // Shown on hover over the numbered regions, e.g. "Human Heavy"
  summary: string;
  // The name of the part the numbering was hoisted from, shown on hover with the summary
  sourceName?: string;
}

interface NumberedRegion {
  // Ungapped residue indices; end is exclusive
  start: number;
  end: number;
}

/**
 * Splits numbering into the regions of consecutive numbered residues, skipping residues with no number.
 */
export function getNumberedRegions(numbering: ResidueNumbering): Array<NumberedRegion> {
  const regions: Array<NumberedRegion> = [];
  let regionStart: number | null = null;
  numbering.labels.forEach((label, i) => {
    if (label != null && regionStart == null) {
      regionStart = i;
    } else if (label == null && regionStart != null) {
      regions.push({start: numbering.start + regionStart, end: numbering.start + i});
      regionStart = null;
    }
  });
  if (regionStart != null) {
    regions.push({start: numbering.start + regionStart, end: numbering.start + numbering.labels.length});
  }
  return regions;
}

interface AntibodyNumberProps {
  value: string;
  gappedIndex: number;
  xFn: TrackProps['xFn'];
  layout: TrackProps['layout'];
}

/**
 * Renders a single antibody index at the correct horizontal position
 */
function AntibodyNumber(props: AntibodyNumberProps): JSX.Element {
  const {value, gappedIndex, xFn, layout} = props;
  const xPos = xFn(gappedIndex);

  const type = new Type(layout.type.fontSize);
  const totalWidth = xFn(gappedIndex + 1) - xPos;
  const textWidth = type.width(value.length);
  const xPad = (totalWidth - textWidth) / 2;

  return (
    <text
      data-gapped-index={gappedIndex}
      transform={`translate(${xPos + xPad} 6)`}
      {...getTestProps({component: 'AntibodyNumbering', element: 'number', key: value})}
      data-amp-safe={false}
    >
      {value}
    </text>
  );
}

function getNumberingTooltip({sourceName, summary}: ResidueNumbering): string {
  return sourceName != null ? `From ${sourceName} · ${summary}` : summary;
}

interface AntibodyNumberedRegionProps {
  layout: AntibodyNumberingLayout;
  numbering: ResidueNumbering;
  onAlignment: boolean;
  realToGapped: Array<number>;
  region: NumberedRegion;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

function AntibodyNumberedRegion(props: AntibodyNumberedRegionProps): JSX.Element | null {
  const {layout, numbering, onAlignment, realToGapped, region, setSelection, sliceStart, sliceEnd, xFn} =
    props;

  // An alignment has a single slice spanning every residue. A sequence map draws only the slice's residues.
  const firstResidue = onAlignment ? region.start : Math.max(region.start, sliceStart);
  const endResidue = onAlignment ? region.end : Math.min(region.end, sliceEnd);
  if (firstResidue >= endResidue) {
    return null;
  }
  const toSliceIndex = (residueIndex: number): number =>
    onAlignment ? realToGapped[residueIndex] : residueIndex;
  const regionStart = toSliceIndex(firstResidue);
  const regionEnd = toSliceIndex(endResidue - 1) + 1;

  // Cannot be replaced with xFn(1) - xFn(0) because xFn is specific to the slice and may not have 1 and 0 in its domain
  const withNumbers = shouldShowNumbers(xFn(regionStart + 1) - xFn(regionStart));
  const tooltip = withNumbers
    ? getNumberingTooltip(numbering)
    : onAlignment
      ? 'Zoom in using the minimap to see antibody numbering'
      : 'Zoom in to see antibody numbering';

  return (
    <>
      <Tooltip label={tooltip} withinPortal={true} disabled={tooltip === ''}>
        <rect
          onClick={() => {
            setSelection(toSliceIndex(region.start), toSliceIndex(region.end - 1) + 1);
          }}
          className={`layer-fill ${css(styles.noFocus)}`}
          fill={BLUE_LIGHTEST}
          fillOpacity={0.7}
          x={xFn(regionStart)}
          y={withNumbers ? REGION_WITH_NUMBERS_TOP : 0}
          width={xFn(regionEnd) - xFn(regionStart)}
          height={withNumbers ? REGION_WITH_NUMBERS_HEIGHT : REGION_WITHOUT_NUMBERS_HEIGHT}
          {...getTestProps({
            component: 'AntibodyNumbering',
            element: 'region',
            key: String(region.start),
          })}
          data-amp-safe={false}
        />
      </Tooltip>
      {withNumbers &&
        range(firstResidue, endResidue - 1).map((residueIndex) => (
          <AntibodyNumber
            key={residueIndex}
            value={numbering.labels[residueIndex - numbering.start]!}
            gappedIndex={toSliceIndex(residueIndex)}
            {...{xFn, layout}}
          />
        ))}
    </>
  );
}

export interface TrackProps {
  layout: AntibodyNumberingLayout;
  numberings: ReadonlyArray<ResidueNumbering>;
  onAlignment: boolean;
  // The residues drawn on the slice, with gaps on an alignment
  residues: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
  // The y-axis position of each numbering, by its index in numberings. Defaults to drawing every numbering
  // at the top of the track, for numberings that never overlap.
  yPositions?: ReadonlyArray<number>;
}

/**
 * Displays antibody numbers above the numbered residues of the slice. Each run of consecutive numbered
 * residues gets its own region, so residues with no number show as gaps. At wider zoom levels, only
 * shows the regions, with a tooltip, in place of individual numbers.
 */
export function AntibodyNumberingTrack({
  layout,
  numberings,
  onAlignment,
  residues,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
  yPositions,
}: TrackProps): JSX.Element {
  assert(onAlignment ? sliceStart === 0 : true, 'Alignments only have one slice, so slice must start at 0');
  const realToGapped = useMemo(() => getRealToGapMap(residues), [residues]);

  return (
    <ComponentWrapper
      layout={layout}
      shouldStopMouseDownPropagation={false}
      dataTestAttrs={{component: 'AntibodyNumbering', element: 'root'}}
    >
      <g fontSize={layout.type.fontSize} className={css(styles.noFocus)}>
        {numberings.map((numbering, numberingIndex) => (
          <g
            key={numberingIndex}
            transform={`translate(0, ${yPositions?.[numberingIndex] ?? 0})`}
            {...getTestProps({
              component: 'AntibodyNumbering',
              element: 'numbering',
              key: String(numberingIndex),
            })}
            data-amp-safe={false}
          >
            {getNumberedRegions(numbering).map((region) => (
              <AntibodyNumberedRegion
                key={region.start}
                {...{
                  layout,
                  numbering,
                  onAlignment,
                  realToGapped,
                  region,
                  setSelection,
                  sliceStart,
                  sliceEnd,
                  xFn,
                }}
              />
            ))}
          </g>
        ))}
      </g>
    </ComponentWrapper>
  );
}

export interface Props {
  layout: AntibodyNumberingLayout;
  onAlignment: boolean;
  redraw: (boxHeight: number) => void;
  residues: string;
  scheme: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays the antibody numbering ANARCI computes for the residues, in the given scheme.
 */
export default function AntibodyNumberingSlice({
  layout,
  onAlignment,
  redraw,
  residues,
  scheme,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: Props): JSX.Element | null {
  const boxHeight = Type.heights[layout.type.fontSize] + layout.vMargin;
  const nonGappedResidues = removeGaps(residues);
  const [numberings, setNumberings] = useState<Array<ValidNumberingResult> | null>(null);
  useEffect(() => {
    const fetchNumbering = async (): Promise<void> => {
      const [response] = await AntibodyAnalysisStore.getNumbering(
        [nonGappedResidues],
        scheme.toLowerCase() as NumberingScheme
      );
      setNumberings(response);
      redraw(response ? boxHeight : 0);
    };
    fetchNumbering();
  }, [boxHeight, nonGappedResidues, scheme, redraw]);

  if (!numberings) {
    return null;
  }

  return (
    <AntibodyNumberingTrack
      numberings={numberings.map((numbering) => ({
        start: numbering.start,
        labels: numbering.indices,
        summary: getMetadataSummary(numbering),
      }))}
      {...{layout, onAlignment, residues, setSelection, sliceStart, sliceEnd, xFn}}
    />
  );
}

const styles = StyleSheet.create({
  noFocus: {
    ':focus': {
      outline: 'none',
    },
  },
});
